import { KARAOKE_TRACK_NUMBER } from '@src/editor/constants';
import { overlapsAnySpan } from '@src/editor/dubbing/clipSpanOverlap';
import {
  dubLineMatchesFragments,
  lineFragmentRanges,
} from '@src/editor/dubbing/DubCoverageService';
import {
  dropSuperseded,
  piecesTouchOnClip,
  recordingSecToClipSec,
} from '@src/editor/dubbing/DubPlacementService';
import {
  buildDubWordTimeline,
  placeTimelineOnClip,
} from '@src/editor/dubbing/dubWordTimeline';
import { KaraokeClip } from '@src/editor/models/karaoke/KaraokeClip';
import { needsSpaceBetweenCaptionRows } from '@src/utility/strings';
import { v4 as uuid } from 'uuid';

import { resolvePunctuationLineBreak } from './captionLineBreakStyle';
import {
  MAX_SEGMENT_CHAR_LENGTH,
  mergeResolvedPhrasesIntoSegments,
  resolveConfiguredMaxCharLength,
} from './karaokeSegmentation';
import {
  computeCaptionUnitSpans,
  computeProportionAtTextPosition,
} from './karaokeWordTiming';

import type { PunctuationLineBreak } from './captionLineBreakStyle';
import type { ClipSpan } from '@src/editor/dubbing/DubPlacementService';
import type {
  ClipWord,
  TimelineSegment,
} from '@src/editor/dubbing/dubWordTimeline';
import type { DubCaptionTiming } from '@src/editor/models/karaoke/KaraokeClip';
import type { KaraokeConfig } from '@src/editor/models/karaoke/KaraokeConfig';
import type { DubCaptionRow, DubRowRecord } from '@src/editor/types/karaoke';
import type { DubManifestDTO } from '@src/repositories/DubbingRepository';
import type { TranslationPhrase } from '@src/types.ts/translation';

/**
 * The dub-timed caption set of a dubbed language (PLAT-11905).
 *
 * What is stored is a ROW RECORD: which character ranges of which dub lines
 * form each caption row. No time is stored. Every time the set is shown, its
 * captions are derived again from the record and the CURRENT dub manifest, so a
 * re-dub moves them and a trim cuts them exactly where it cuts the dub audio.
 *
 * - Rows are split by the same rules translated captions use today
 *   (`mergeResolvedPhrasesIntoSegments`, with the configured line length and
 *   punctuation style), one unit per stored dub word.
 * - A trim never loses a row: rows of lines the clip no longer shows stay in the
 *   record and simply give no caption.
 * - A re-dub that changed a line's words rebuilds that line's rows (its manual
 *   breaks are lost); every other line keeps its rows.
 */

/**
 * The translation fragment a dub word belongs to, named the way a source row's
 * caption phrases name theirs (`leadUuid`, `initialIndex`), or `undefined` when
 * it cannot be named safely.
 */
export type DubWordIdentityResolver = (
  segmentId: string,
  charStart: number,
  charEnd: number,
) => { leadUuid?: string; initialIndex?: number } | undefined;

/** The dub, on one clip: its lines timed, and their words placed on the clip. */
export interface DubCaptionInputs {
  timeline: TimelineSegment[];
  placed: Map<string, ClipWord[]>;
  spans: ClipSpan[];
  /**
   * Every line the dub still has (superseded copies dropped), placeable or
   * not. A line missing from `timeline` but listed here has no signed address
   * in this fetch: it still exists, so its rows are kept until it can be
   * placed again.
   */
  existingSegmentIds: ReadonlySet<string>;
}

export function dubCaptionInputs(dub: {
  manifest: DubManifestDTO;
  renditionUrls: Record<string, string>;
  spans: ClipSpan[];
}): DubCaptionInputs {
  const timeline = buildDubWordTimeline(dub.manifest, dub.renditionUrls);
  return {
    timeline,
    placed: placeTimelineOnClip(timeline, dub.spans),
    spans: dub.spans,
    existingSegmentIds: new Set(
      dropSuperseded(dub.manifest.segments ?? []).live.map(
        segment => segment.segmentId,
      ),
    ),
  };
}

/** The row rules a caption config asks for. */
export interface DubRowOptions {
  maxCharLength: number;
  punctuationLineBreak?: PunctuationLineBreak;
}

export function dubRowOptions(config: KaraokeConfig): DubRowOptions {
  return {
    // Unset means the segmenter's default line length, as for every caption.
    maxCharLength:
      resolveConfiguredMaxCharLength(config.transcript_maximum_length) ??
      MAX_SEGMENT_CHAR_LENGTH,
    punctuationLineBreak: resolvePunctuationLineBreak(config.line_break_style),
  };
}

/**
 * Split each of the given lines into caption rows. One unit per stored word
 * (or one unit for a line with no stored words), fed to the translated row
 * merger; each merged row becomes one character range of the line.
 */
export function buildDubRows(
  segments: readonly TimelineSegment[],
  options: DubRowOptions,
): DubCaptionRow[] {
  const rows: DubCaptionRow[] = [];
  for (const segment of segments) {
    if (segment.words.length === 0) continue;
    const units = segment.words.map((word, index) => ({
      text: word.text,
      ts: word.startMs / 1000,
      endTs: word.endMs / 1000,
      bucket: [],
      originalIndices: [index],
    }));
    const merged = mergeResolvedPhrasesIntoSegments(
      units,
      options.maxCharLength,
      options.punctuationLineBreak,
    );
    // Each merged row names its units; an over-long full-width unit comes back
    // as several pieces of the same unit, each a slice of its text, found in
    // order.
    let cursor = 0;
    for (const row of merged) {
      let start = Infinity;
      let end = -Infinity;
      for (const piece of row.translatedPhrases ?? []) {
        const unit = segment.words[piece.originalIndices?.[0] ?? -1];
        if (!unit) continue;
        const from = Math.max(cursor, unit.charStart);
        const found = segment.spokenText.indexOf(piece.text, from);
        const pieceStart = found >= 0 ? found : unit.charStart;
        const pieceEnd = found >= 0 ? found + piece.text.length : unit.charEnd;
        start = Math.min(start, pieceStart);
        end = Math.max(end, pieceEnd);
        cursor = pieceEnd;
      }
      if (start < end) {
        rows.push({
          id: uuid(),
          parts: [{ segmentId: segment.segmentId, start, end }],
        });
      }
    }
  }
  return rows;
}

function overlapsClip(segment: TimelineSegment, spans: ClipSpan[]): boolean {
  return overlapsAnySpan(segment.startMs / 1000, segment.endMs / 1000, spans);
}

/** Rows in reading order: by their first line's start, then by position. */
function sortRows(
  rows: DubCaptionRow[],
  startOf: Map<string, number>,
): DubCaptionRow[] {
  const key = (row: DubCaptionRow): [number, number] => {
    const first = row.parts[0];
    return [
      startOf.get(first?.segmentId ?? '') ?? Number.MAX_SAFE_INTEGER,
      first?.start ?? 0,
    ];
  };
  return [...rows].sort((a, b) => {
    const [aStart, aPos] = key(a);
    const [bStart, bPos] = key(b);
    return aStart - bStart || aPos - bPos;
  });
}

/** The record for every placeable line the clip overlaps. */
export function buildDubRecord(
  language: string,
  inputs: DubCaptionInputs,
  options: DubRowOptions,
): DubRowRecord {
  const lines = inputs.timeline.filter(segment =>
    overlapsClip(segment, inputs.spans),
  );
  const segmentTexts: Record<string, string> = {};
  for (const line of lines) segmentTexts[line.segmentId] = line.spokenText;
  return { language, segmentTexts, rows: buildDubRows(lines, options) };
}

/**
 * Bring the record up to date with the CURRENT dub, keeping manual breaks:
 *
 * 1. A line whose words changed (its rebuilt text differs from the one its rows
 *    were built from), or that the dub no longer has: its rows are removed — a
 *    joined row that includes it keeps its other parts — and, if the line still
 *    exists, it is split into rows again.
 * 2. A line the clip overlaps that has no rows yet gets rows.
 * 3. Nothing else changes: no row is removed or re-split because of a trim, a
 *    template or an end card.
 *
 * Returns the record it was handed when nothing changed.
 */
export function reconcileDubRecord(
  record: DubRowRecord,
  inputs: DubCaptionInputs,
  options: DubRowOptions,
): DubRowRecord {
  const byId = new Map(inputs.timeline.map(line => [line.segmentId, line]));
  const referenced = new Set<string>(Object.keys(record.segmentTexts));
  for (const row of record.rows) {
    for (const part of row.parts) referenced.add(part.segmentId);
  }

  // A line that cannot be placed in this fetch (no signed address) but that
  // the dub still has is neither changed nor gone: its rows wait for it.
  const changed = new Set<string>();
  for (const segmentId of referenced) {
    const line = byId.get(segmentId);
    if (line) {
      if (line.spokenText !== record.segmentTexts[segmentId]) {
        changed.add(segmentId);
      }
    } else if (!inputs.existingSegmentIds.has(segmentId)) {
      changed.add(segmentId);
    }
  }

  let rows = record.rows;
  if (changed.size > 0) {
    rows = [];
    for (const row of record.rows) {
      const kept = row.parts.filter(part => !changed.has(part.segmentId));
      if (kept.length === row.parts.length) rows.push(row);
      else if (kept.length > 0) rows.push({ ...row, parts: kept });
    }
  }

  const withRows = new Set<string>();
  for (const row of rows) {
    for (const part of row.parts) withRows.add(part.segmentId);
  }
  const toBuild = inputs.timeline.filter(
    line =>
      !withRows.has(line.segmentId) &&
      (changed.has(line.segmentId) || overlapsClip(line, inputs.spans)),
  );

  if (changed.size === 0 && toBuild.length === 0) return record;

  const segmentTexts = { ...record.segmentTexts };
  for (const segmentId of changed) {
    const line = byId.get(segmentId);
    if (line) segmentTexts[segmentId] = line.spokenText;
    else delete segmentTexts[segmentId];
  }
  for (const line of toBuild) segmentTexts[line.segmentId] = line.spokenText;

  const startOf = new Map(
    inputs.timeline.map(line => [line.segmentId, line.startMs]),
  );
  return {
    language: record.language,
    segmentTexts,
    rows: sortRows([...rows, ...buildDubRows(toBuild, options)], startOf),
  };
}

/** One shown piece of a row: a word, or the part of it the row's range covers. */
interface ShownPiece {
  segmentId: string;
  /** Where the piece sits in its line's text, `[charStart, charEnd)`. */
  charStart: number;
  charEnd: number;
  text: string;
  startSec: number;
  endSec: number;
  stretchIndex: number;
  /** Does the piece begin, or end, its whole line? */
  beginsLine: boolean;
  endsLine: boolean;
  /** Whitespace the caption's text has after this piece. */
  separator: string;
}

/** The part of a placed word that lies inside `[start, end)` of its line. */
function pieceOfWord(
  word: ClipWord,
  start: number,
  end: number,
  spokenText: string,
): Omit<
  ShownPiece,
  | 'beginsLine'
  | 'endsLine'
  | 'separator'
  | 'segmentId'
  | 'charStart'
  | 'charEnd'
> | null {
  const from = Math.max(word.charStart, start);
  const to = Math.min(word.charEnd, end);
  if (from >= to) return null;
  if (from === word.charStart && to === word.charEnd) {
    return {
      text: word.text,
      startSec: word.startSec,
      endSec: word.endSec,
      stretchIndex: word.stretchIndex,
    };
  }
  // A range that cuts inside a word (an over-long full-width word, or a line
  // with no stored words) takes its share of the word's time by visual width.
  const span = word.endSec - word.startSec;
  const startProportion = computeProportionAtTextPosition(
    word.text,
    from - word.charStart,
  );
  const endProportion = computeProportionAtTextPosition(
    word.text,
    to - word.charStart,
  );
  return {
    text: spokenText.slice(from, to).trim(),
    startSec: word.startSec + span * startProportion,
    endSec: word.startSec + span * endProportion,
    stretchIndex: word.stretchIndex,
  };
}

/** What the caption's text has between two pieces of the same line. */
function separatorBetween(gap: string): string {
  if (/^\s*$/.test(gap)) return gap;
  // Words between the two were cut by the clip: space them as the line does.
  return /\s/.test(gap) ? ' ' : '';
}

function shownPiecesOfRow(
  row: DubCaptionRow,
  lines: Map<string, TimelineSegment>,
  placed: Map<string, ClipWord[]>,
): ShownPiece[] {
  const pieces: ShownPiece[] = [];
  for (const part of row.parts) {
    const line = lines.get(part.segmentId);
    const words = placed.get(part.segmentId);
    if (!line || !words || line.words.length === 0) continue;
    const lineFirst = line.words[0].charStart;
    const lineLast = line.words[line.words.length - 1].charEnd;
    const partPieces: ShownPiece[] = [];
    for (const word of words) {
      const piece = pieceOfWord(word, part.start, part.end, line.spokenText);
      if (!piece || piece.text.length === 0) continue;
      const from = Math.max(word.charStart, part.start);
      const to = Math.min(word.charEnd, part.end);
      partPieces.push({
        ...piece,
        segmentId: part.segmentId,
        charStart: from,
        charEnd: to,
        beginsLine: from === lineFirst,
        endsLine: to === lineLast,
        separator: '',
      });
    }
    partPieces.forEach((piece, i) => {
      const next = partPieces[i + 1];
      if (next) {
        piece.separator = separatorBetween(
          line.spokenText.slice(piece.charEnd, next.charStart),
        );
      }
    });
    if (pieces.length > 0 && partPieces.length > 0) {
      const previous = pieces[pieces.length - 1];
      previous.separator = needsSpaceBetweenCaptionRows(
        previous.text,
        partPieces[0].text,
      )
        ? ' '
        : '';
    }
    pieces.push(...partPieces);
  }
  return pieces;
}

/** Where a line's own audio starts and ends inside one kept stretch, on the clip. */
export function lineEdgesOnClip(
  line: TimelineSegment,
  span: ClipSpan,
): { startSec: number; endSec: number } {
  const spanEndSec = span.sourceStartSec + span.durationSec;
  return {
    startSec: recordingSecToClipSec(
      Math.max(line.startMs / 1000, span.sourceStartSec),
      span,
    ),
    endSec: recordingSecToClipSec(
      Math.min(line.endMs / 1000, spanEndSec),
      span,
    ),
  };
}

function stretchesTouch(
  spans: readonly ClipSpan[],
  a: number,
  b: number,
): boolean {
  if (a === b) return true;
  const first = spans[a];
  const second = spans[b];
  if (!first || !second) return false;
  return piecesTouchOnClip(
    { timeSec: first.clipStartSec, durationSec: first.durationSec },
    { timeSec: second.clipStartSec },
  );
}

interface DerivedCaption {
  id: string;
  row: DubCaptionRow;
  timing: DubCaptionTiming;
  text: string;
  firstSegmentId: string;
  lastSegmentId: string;
  isFirstRun: boolean;
  isLastRun: boolean;
}

/**
 * The captions the record shows on this clip, timed from the current dub.
 *
 * - One caption per run of a row's shown words whose stretches touch on the
 *   clip clock, so a plain split of the video never breaks a caption. A later
 *   run of one row gets the id `<row id>~2`; nothing about runs is stored.
 * - A row with no shown word gives no caption and stays in the record.
 * - A caption starts at its first shown word, or at its line's start when it
 *   begins its line; it ends at its last shown word, or at its line's end when
 *   it ends its line, and runs on to the next caption's start when that caption
 *   continues the same line. So each line's captions start and end with its
 *   dubbed audio, Slide-moved lines included.
 */
export function deriveDubCaptionClips(
  record: DubRowRecord,
  inputs: DubCaptionInputs,
  configFor: (configId: string | undefined) => KaraokeConfig,
  wordIdentity?: DubWordIdentityResolver,
): KaraokeClip[] {
  const lines = new Map(inputs.timeline.map(line => [line.segmentId, line]));
  const captions: DerivedCaption[] = [];
  const speakerOf = (segmentId: string): string[] => {
    const speaker = lines.get(segmentId)?.speaker;
    return speaker ? [speaker] : [];
  };
  // Speakers of the rows passed over since the last row that shows a word: a
  // join of the captions on either side takes those rows in.
  let hiddenSpeakers = new Set<string>();

  for (const row of record.rows) {
    const pieces = shownPiecesOfRow(row, lines, inputs.placed);
    if (pieces.length === 0) {
      for (const part of row.parts) {
        for (const speaker of speakerOf(part.segmentId)) {
          hiddenSpeakers.add(speaker);
        }
      }
      continue;
    }
    const hiddenSpeakersBefore = [...hiddenSpeakers];
    hiddenSpeakers = new Set();

    const runs: ShownPiece[][] = [[pieces[0]]];
    for (let i = 1; i < pieces.length; i++) {
      const previous = pieces[i - 1];
      if (
        stretchesTouch(
          inputs.spans,
          previous.stretchIndex,
          pieces[i].stretchIndex,
        )
      ) {
        runs[runs.length - 1].push(pieces[i]);
      } else {
        runs.push([pieces[i]]);
      }
    }

    runs.forEach((run, runIndex) => {
      const first = run[0];
      const last = run[run.length - 1];
      const firstLine = lines.get(first.segmentId);
      const lastLine = lines.get(last.segmentId);
      const startSec =
        first.beginsLine && firstLine
          ? lineEdgesOnClip(firstLine, inputs.spans[first.stretchIndex])
              .startSec
          : first.startSec;
      const endSec =
        last.endsLine && lastLine
          ? lineEdgesOnClip(lastLine, inputs.spans[last.stretchIndex]).endSec
          : last.endSec;
      const words = run.map((piece, i) => ({
        text: piece.text,
        separator: i < run.length - 1 ? piece.separator : '',
        startSec: piece.startSec,
        endSec: piece.endSec,
        segmentId: piece.segmentId,
        charStart: piece.charStart,
        charEnd: piece.charEnd,
        ...wordIdentity?.(piece.segmentId, piece.charStart, piece.charEnd),
      }));
      captions.push({
        id: runIndex === 0 ? row.id : `${row.id}~${runIndex + 1}`,
        row,
        timing: {
          startSec: Math.min(startSec, first.startSec),
          endSec: Math.max(endSec, last.endSec),
          words,
          speakers: [
            ...new Set(run.flatMap(piece => speakerOf(piece.segmentId))),
          ],
          ...(runIndex === 0 && hiddenSpeakersBefore.length > 0
            ? { hiddenSpeakersBefore }
            : {}),
        },
        text: words.map(word => word.text + word.separator).join(''),
        firstSegmentId: first.segmentId,
        lastSegmentId: last.segmentId,
        isFirstRun: runIndex === 0,
        isLastRun: runIndex === runs.length - 1,
      });
    });
  }

  captions.sort((a, b) => a.timing.startSec - b.timing.startSec);
  // A caption runs on to the next one of the same line, so a line's captions
  // cover its audio without a gap.
  for (let i = 0; i < captions.length - 1; i++) {
    const current = captions[i];
    const next = captions[i + 1];
    if (
      next.firstSegmentId === current.lastSegmentId &&
      next.timing.startSec > current.timing.endSec
    ) {
      current.timing.endSec = next.timing.startSec;
    }
  }

  return captions.map(
    caption =>
      new KaraokeClip({
        id: caption.id,
        transcriptTokens: [],
        karaokeConfig: configFor(caption.row.karaokeConfigId),
        track: KARAOKE_TRACK_NUMBER,
        translatedText: caption.text,
        dubRowId: caption.row.id,
        dubTiming: caption.timing,
        startSecondsOffset: caption.isFirstRun
          ? caption.row.startSecondsOffset
          : undefined,
        endSecondsOffset: caption.isLastRun
          ? caption.row.endSecondsOffset
          : undefined,
      }),
  );
}

// ─── Edits to the row record (PLAT-11905) ───────────────
//
// A dub-timed caption is edited in its row record, never on the shown clip: the
// clips are derived again from the record on every re-fit, so an edit written
// on a clip would be lost. Each edit returns a new record, or `null` when it
// cannot be made; the record it was handed is never changed in place.

/** Where a row is split: a character position in one dub line's text. */
export interface DubSplitPoint {
  segmentId: string;
  position: number;
}

/**
 * The place in its dub line that a line-break position of the captions panel
 * names, or `null` when it names none that a row may break at.
 *
 * The panel counts a row's text phrase by phrase — one phrase per shown word on
 * a dub-timed caption — with ONE joining character between phrases, whether or
 * not a space shows there (`TranslatedCaptionsView`'s `buildLineModel`), and
 * offers a break at the start of every word and, inside a Chinese or Japanese
 * word, between any two characters. This undoes that count against the
 * caption's own words.
 */
export function dubCaptionSplitPoint(
  words: DubCaptionTiming['words'],
  textPosition: number,
): DubSplitPoint | null {
  let viewStart = 0;
  for (let i = 0; i < words.length; i++) {
    const word = words[i];
    const offset = textPosition - viewStart;
    if (offset >= 0 && offset < word.text.length) {
      // The start of the row is not a break.
      if (i === 0 && offset === 0) return null;
      const breakable =
        offset === 0 ||
        computeCaptionUnitSpans(word.text).some(unit => unit.start === offset);
      if (!breakable) return null;
      return { segmentId: word.segmentId, position: word.charStart + offset };
    }
    viewStart += word.text.length + 1;
  }
  return null;
}

/**
 * Split a row in two at a point inside it. The left row keeps the row's id and
 * start offset; the right row is new and takes the end offset. A point at the
 * row's very start, or outside it, splits nothing.
 */
export function splitDubRow(
  record: DubRowRecord,
  rowId: string,
  at: DubSplitPoint,
): DubRowRecord | null {
  const index = record.rows.findIndex(row => row.id === rowId);
  if (index < 0) return null;
  const row = record.rows[index];
  const k = row.parts.findIndex(
    part =>
      part.segmentId === at.segmentId &&
      part.start <= at.position &&
      at.position < part.end,
  );
  if (k < 0) return null;
  const part = row.parts[k];
  if (k === 0 && at.position === part.start) return null;

  const leftParts =
    at.position === part.start
      ? row.parts.slice(0, k)
      : [...row.parts.slice(0, k), { ...part, end: at.position }];
  const rightParts =
    at.position === part.start
      ? row.parts.slice(k)
      : [{ ...part, start: at.position }, ...row.parts.slice(k + 1)];

  const left: DubCaptionRow = {
    id: row.id,
    parts: leftParts,
    ...(row.karaokeConfigId ? { karaokeConfigId: row.karaokeConfigId } : {}),
    ...(row.startSecondsOffset !== undefined
      ? { startSecondsOffset: row.startSecondsOffset }
      : {}),
  };
  const right: DubCaptionRow = {
    id: uuid(),
    parts: rightParts,
    ...(row.karaokeConfigId ? { karaokeConfigId: row.karaokeConfigId } : {}),
    ...(row.endSecondsOffset !== undefined
      ? { endSecondsOffset: row.endSecondsOffset }
      : {}),
  };
  const rows = [...record.rows];
  rows.splice(index, 1, left, right);
  return { ...record, rows };
}

/**
 * Join the right row onto the end of the left row — across dub lines too
 * (decided 2026-09-30: joins across dub lines are allowed, and a joined row
 * takes its time from both lines). Two touching ranges of the same line
 * become one again, so joining what a split separated
 * restores the row as it was. The joined row keeps the left row's id, style and
 * start offset, and the right row's end offset.
 *
 * Rows the record holds BETWEEN the two — rows whose words the clip cuts out,
 * which a trim never removes — are taken into the join, so if their words come
 * back they read in order inside the joined row instead of as a second caption
 * overlapping it.
 */
export function joinDubRows(
  record: DubRowRecord,
  leftRowId: string,
  rightRowId: string,
): DubRowRecord | null {
  if (leftRowId === rightRowId) return null;
  const leftIndex = record.rows.findIndex(row => row.id === leftRowId);
  const rightIndex = record.rows.findIndex(row => row.id === rightRowId);
  if (leftIndex < 0 || rightIndex <= leftIndex) return null;
  const left = record.rows[leftIndex];
  const right = record.rows[rightIndex];
  const taken = record.rows.slice(leftIndex + 1, rightIndex + 1);

  const parts = left.parts.map(part => ({ ...part }));
  for (const part of taken.flatMap(row => row.parts)) {
    const last = parts[parts.length - 1];
    const gap =
      last && last.segmentId === part.segmentId && last.end <= part.start
        ? (record.segmentTexts[part.segmentId] ?? '').slice(
            last.end,
            part.start,
          )
        : null;
    if (last && gap !== null && /^\s*$/.test(gap)) {
      last.end = part.end;
    } else {
      parts.push({ ...part });
    }
  }

  const joined: DubCaptionRow = {
    id: left.id,
    parts,
    ...(left.karaokeConfigId ? { karaokeConfigId: left.karaokeConfigId } : {}),
    ...(left.startSecondsOffset !== undefined
      ? { startSecondsOffset: left.startSecondsOffset }
      : {}),
    ...(right.endSecondsOffset !== undefined
      ? { endSecondsOffset: right.endSecondsOffset }
      : {}),
  };
  const takenIds = new Set(taken.map(row => row.id));
  return {
    ...record,
    rows: record.rows
      .filter(row => !takenIds.has(row.id))
      .map(row => (row.id === leftRowId ? joined : row)),
  };
}

/** Write a row's time offsets; `undefined` clears one. */
export function setDubRowOffsets(
  record: DubRowRecord,
  rowId: string,
  offsets: { startSecondsOffset?: number; endSecondsOffset?: number },
): DubRowRecord | null {
  const row = record.rows.find(candidate => candidate.id === rowId);
  if (!row) return null;
  const { startSecondsOffset: _start, endSecondsOffset: _end, ...rest } = row;
  const next: DubCaptionRow = {
    ...rest,
    ...(offsets.startSecondsOffset !== undefined
      ? { startSecondsOffset: offsets.startSecondsOffset }
      : {}),
    ...(offsets.endSecondsOffset !== undefined
      ? { endSecondsOffset: offsets.endSecondsOffset }
      : {}),
  };
  return {
    ...record,
    rows: record.rows.map(candidate =>
      candidate.id === rowId ? next : candidate,
    ),
  };
}

/**
 * Name each dub word's translation fragment (PLAT-11905),
 * so Fix Translation and the edited-word mark work on dub-timed captions.
 *
 * Only for a line whose words can be trusted to sit where the translation's
 * fragments say (`dubLineMatchesFragments`). Any other line's words get no
 * identity rather than a wrong one.
 */
export function dubWordIdentityResolver(
  inputs: DubCaptionInputs,
  fragments: readonly TranslationPhrase[],
  elements: Parameters<typeof lineFragmentRanges>[1],
): DubWordIdentityResolver | undefined {
  if (fragments.length === 0) return undefined;
  const rebuilt = lineFragmentRanges([...fragments], elements);
  const trusted = new Map<
    string,
    { phrase: TranslationPhrase; start: number; end: number }[]
  >();
  for (const line of inputs.timeline) {
    const now = rebuilt.get(line.segmentId);
    if (!now || !dubLineMatchesFragments(line, now)) continue;
    trusted.set(line.segmentId, now.fragments);
  }
  return (segmentId, charStart, charEnd) => {
    const fragment = trusted
      .get(segmentId)
      ?.find(range => range.start <= charStart && charEnd <= range.end);
    if (!fragment) return undefined;
    const leadUuid = fragment.phrase.originalElementIds?.[0];
    const initialIndex = fragment.phrase.originalIndices[0];
    return {
      ...(leadUuid !== undefined ? { leadUuid } : {}),
      ...(initialIndex !== undefined ? { initialIndex } : {}),
    };
  };
}
